import type { SubmitHandler } from "react-hook-form"
import { useState } from "react"
import { navigate } from "gatsby"
import { useDispatch } from "react-redux"
import SingleInputForm from "@/components/SingleInputForm"
import { setActiveUser } from "@/redux/features/activeUserSlice"
import { openErrorModal } from "@/redux/features/errorModalSlice"
import { useAppSelector } from "@/redux/hooks"
import { cmAddSubscriber } from "@/services/campaignMonitor"
import { getIpQualityScore } from "@/services/getIpQualityScore"
import { vlRegister } from "@/services/viralLoops"

interface SingleInputSignupFormProps {
  wrapperClass?: string
  size?: "small" | "large"
  formName: string
  submitBtnText?: string
}

export default function SingleInputSignupForm({
  wrapperClass,
  size,
  formName,
  submitBtnText = "Try it",
}: SingleInputSignupFormProps) {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const { products } = useAppSelector(state => state.products)

  const handleSubmit: SubmitHandler<{
    emailAddress: string
    phoneNumber?: string
  }> = async formData => {
    const emailAddress = formData.emailAddress
    setLoading(true)

    try {
      // IP Fraud Check
      const ipqs: any = await getIpQualityScore()
      if (!ipqs.data.pass) {
        return false
      }

      const customFieldData: Array<Record<string, any>> = [
        { "Which Form": formName },
        { "Subscriber IP Address": ipqs.data.ip },
        { "Fraud Risk": ipqs.data.ipqs?.fraud_score || "n/a" },
      ]

      // Register with ViralLoops
      const vlRes = await vlRegister(emailAddress, null)
      customFieldData.push({ "Share URL": vlRes.data.shareUrl })

      // Find default email product
      const defaultEmailProduct = products.find(
        p => p.productId === "email_dailyDonut",
      )

      if (!defaultEmailProduct) {
        throw new Error("No default email product found")
      }

      // Add to Campaign Monitor
      const res = await cmAddSubscriber(
        emailAddress,
        defaultEmailProduct.subscribeId,
        customFieldData,
      )
      setLoading(false)

      if (res.error) {
        dispatch(openErrorModal([res.error]))
      } else {
        dispatch(
          setActiveUser({
            email: emailAddress,
            shareUrl: vlRes.data.shareUrl,
            ambassadorPermission: true,
          }),
        )
        navigate("/welcome")
      }
    } catch (err) {
      console.error("Subscription error:", err)
      const errorMessage =
        err instanceof Error ? err.message : "An unknown error occurred"
      dispatch(openErrorModal([errorMessage]))
      setLoading(false)
    }
  }

  return (
    <SingleInputForm
      loading={loading}
      onSubmit={handleSubmit}
      onError={console.error}
      submitBtnText={submitBtnText}
      wrapperClass={wrapperClass}
      size={size}
    />
  )
}
